import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import { keyGenClient } from '@/api/clients/keyGenClient';
import { NetworkError, ServerError } from '@/api/errors';
import type { KeyGenHeader, KeyGenRequest } from '@/api/types/keyGenTypes';
import { useUserInfo } from '@/features/auth/hooks/useUserInfo';
import { isTokenAuthenticated } from '@/features/auth/isTokenAuthenticated';

export function useKeyGenQuery(keyGenRequest: KeyGenRequest): UseQueryResult<KeyGenHeader[]> {
  // Doing the same thing other queries are doing, but we should be just using a single method to check if user isAuthenticated
  const { accessToken } = useUserInfo();
  const isAuthenticated = isTokenAuthenticated(accessToken);

  const isEnabled =
    !isAuthenticated || Boolean(keyGenRequest.user_entitlement && keyGenRequest.user_onboarding_preferences);

  return useQuery({
    retry: (failureCount, error) => {
      switch (error.constructor) {
        case NetworkError:
        case ServerError:
          return failureCount < 3;
      }

      return false;
    },
    throwOnError: false,
    queryKey: ['useKeyGenQuery', keyGenRequest, accessToken],
    queryFn: async () => {
      const headers = await keyGenClient.getHeaders(keyGenRequest);

      console.debug('[useKeyGenQuery] headers:', headers);

      return headers;
    },
    enabled: isEnabled,
  });
}
